import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  Stack,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import type { CourtPackageDto, CourtPackageVerificationDto } from '@munifocus/contracts-gov';
import {
  isBuilding,
  useCourtPackages,
  useDownloadCourtPackage,
  useRequestCourtPackage,
  useVerifyStoredPackage,
  useVerifyUploadedPackage,
} from '../lib/court-packages-api';
import { formatDateTime } from '../lib/relative-time';

function megabytes(bytes: number | null): string {
  return bytes === null ? '' : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_TONE = {
  queued: 'pending',
  building: 'pending',
  ready: 'success',
  failed: 'emergency',
} as const;

/**
 * The court / disclosure package for this file: build one, download it, and
 * check a package — the stored one, or a copy brought back from court — still
 * matches what was built.
 *
 * Rendered only where the municipality has the module (`enabled`); the build,
 * download and verify buttons only for someone who holds
 * `court_package:export` on the file. The API enforces both regardless.
 */
export function CourtPackagePanel({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const packages = useCourtPackages(fileId);
  const request = useRequestCourtPackage(fileId);
  const download = useDownloadCourtPackage(fileId);
  const verifyStored = useVerifyStoredPackage(fileId);
  const verifyUpload = useVerifyUploadedPackage(fileId);
  const [result, setResult] = useState<CourtPackageVerificationDto | null>(null);
  const upload = useRef<HTMLInputElement>(null);

  const data = packages.data;
  if (!data?.enabled) return null;
  const building = data.packages.some(isBuilding);

  const onVerifyStored = (pkg: CourtPackageDto) =>
    verifyStored.mutate(pkg.id, { onSuccess: (r) => setResult(r) });
  const onUpload = (file: File | undefined) => {
    if (!file) return;
    verifyUpload.mutate(file, { onSuccess: (r) => setResult(r) });
    if (upload.current) upload.current.value = '';
  };

  return (
    <HairlineCard sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Box>
            <Typography
              component="h2"
              sx={{ fontFamily: fontStacks.display, fontWeight: 700, fontSize: 16 }}
            >
              {t('gov.courtPackage.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('gov.courtPackage.subtitle')}
            </Typography>
          </Box>
          {data.canExport && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<Icons.ShieldCheck size={iconSize.sm} />}
                onClick={() => upload.current?.click()}
                disabled={verifyUpload.isPending}
              >
                {t('gov.courtPackage.verifyCopy')}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<Icons.Archive size={iconSize.sm} />}
                onClick={() => request.mutate()}
                disabled={request.isPending || building}
              >
                {t('gov.courtPackage.build')}
              </Button>
              <input
                ref={upload}
                type="file"
                accept=".zip,application/zip"
                hidden
                aria-label={t('gov.courtPackage.verifyCopy')}
                onChange={(e) => onUpload(e.target.files?.[0])}
              />
            </Stack>
          )}
        </Stack>

        {packages.isError && <Alert severity="error">{t('gov.courtPackage.loadError')}</Alert>}
        {data.packages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.courtPackage.empty')}
          </Typography>
        )}

        {data.packages.map((pkg) => (
          <HairlineCard key={pkg.id} sx={{ p: 1.5 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
            >
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusChip
                    status={STATUS_TONE[pkg.status]}
                    label={t(`gov.courtPackage.status.${pkg.status}`)}
                  />
                  <Typography variant="body2">
                    {formatDateTime(pkg.createdAt, i18n.language)}
                    {pkg.requestedByName ? ` · ${pkg.requestedByName}` : ''}
                  </Typography>
                  {isBuilding(pkg) && (
                    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                      <CircularProgress
                        size={16}
                        thickness={5}
                        variant={pkg.progress > 0 ? 'determinate' : 'indeterminate'}
                        value={pkg.progress}
                        aria-label={t('gov.courtPackage.progress', { percent: pkg.progress })}
                        sx={{ color: palette.brand.accent }}
                      />
                      <Typography variant="caption" color="text.secondary" aria-live="polite">
                        {t('gov.courtPackage.progress', { percent: pkg.progress })}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                {pkg.status === 'ready' && (
                  <Typography variant="caption" color="text.secondary">
                    {t('gov.courtPackage.summary', {
                      count: pkg.entryCount ?? 0,
                      size: megabytes(pkg.sizeBytes),
                    })}
                  </Typography>
                )}
                {pkg.status === 'ready' && pkg.zipSha256 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: fontStacks.mono, wordBreak: 'break-all' }}
                  >
                    SHA-256 {pkg.zipSha256}
                  </Typography>
                )}
                {pkg.integrityWarnings > 0 && (
                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                    {t('gov.courtPackage.integrityWarnings', { count: pkg.integrityWarnings })}
                  </Alert>
                )}
                {pkg.status === 'failed' && pkg.error && (
                  <Typography variant="caption" color="error">
                    {pkg.error}
                  </Typography>
                )}
              </Stack>
              {pkg.status === 'ready' && data.canExport && (
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    size="small"
                    startIcon={<Icons.ShieldCheck size={iconSize.sm} />}
                    onClick={() => onVerifyStored(pkg)}
                    disabled={verifyStored.isPending}
                  >
                    {t('gov.courtPackage.verify')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icons.Download size={iconSize.sm} />}
                    onClick={() => download.mutate(pkg.id)}
                    disabled={download.isPending}
                  >
                    {t('gov.courtPackage.download')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </HairlineCard>
        ))}
      </Stack>

      <VerificationDialog result={result} onClose={() => setResult(null)} />
    </HairlineCard>
  );
}

function VerificationDialog({
  result,
  onClose,
}: {
  result: CourtPackageVerificationDto | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const failures = result?.entries.filter((e) => e.outcome !== 'match') ?? [];
  return (
    <Dialog open={Boolean(result)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('gov.courtPackage.verification.title')}</DialogTitle>
      <DialogContent>
        {result && (
          <Stack spacing={2}>
            <Alert severity={result.ok ? 'success' : 'error'}>
              {result.ok
                ? t('gov.courtPackage.verification.ok', { count: result.entries.length })
                : t('gov.courtPackage.verification.failed')}
            </Alert>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                {result.manifestMatchesRecord
                  ? t('gov.courtPackage.verification.manifestMatches')
                  : t('gov.courtPackage.verification.manifestDiffers')}
              </Typography>
              {result.archiveMatchesRecord !== null && (
                <Typography variant="body2" color="text.secondary">
                  {result.archiveMatchesRecord
                    ? t('gov.courtPackage.verification.archiveMatches')
                    : t('gov.courtPackage.verification.archiveDiffers')}
                </Typography>
              )}
            </Stack>
            {failures.length > 0 && (
              <Table size="small" aria-label={t('gov.courtPackage.verification.failures')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('gov.courtPackage.verification.file')}</TableCell>
                    <TableCell>{t('gov.courtPackage.verification.outcome')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failures.map((e) => (
                    <TableRow key={e.path}>
                      <TableCell sx={{ fontFamily: fontStacks.mono, wordBreak: 'break-all' }}>
                        {e.exhibit ? `${e.exhibit} · ` : ''}
                        {e.path}
                      </TableCell>
                      <TableCell>
                        {t(`gov.courtPackage.verification.outcomes.${e.outcome}`)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
